import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import InlineLoading from "src/components/common/InlineLoading/InlineLoading";
import { NoteTypesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as NoteTypeActions from "../../../actions/noteType";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./NoteTypesPage.scss";

interface NoteTypesPageProps {
    noteTypes: Dtos.NoteType[];
    formFilter: IFormFilter;
    loadingNoteTypes: boolean;
    loadNoteTypesSuccess: boolean;
    loadNoteTypesFailure: boolean;
    loadNoteTypesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearNoteTypes: NoteTypeActions.INoteTypeClearActionCreator;
    loadNoteTypes: NoteTypeActions.INoteTypeLoadActionCreator;
    filter: NoteTypeActions.INoteTypeFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const NoteTypesPage: FC<NoteTypesPageProps> = ({
    noteTypes,
    formFilter,
    loadingNoteTypes,
    loadNoteTypesSuccess,
    loadNoteTypesFailure,
    loadNoteTypesErrorCode,
    permissions,
    pageSize,
    clearNoteTypes,
    loadNoteTypes,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showNoteTypes = !loadingNoteTypes && loadNoteTypesSuccess;


    // Load note types
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(NoteTypesBreadcrumbs());

            loadNoteTypes();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        NoteTypesBreadcrumbs,
        loadNoteTypes,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadNoteTypesFailure) {
            navigate(`/error/${loadNoteTypesErrorCode}`);
        }
    }, [loadNoteTypesFailure, loadNoteTypesErrorCode, navigate]);

    // Cleanup effect to clear the note types on component unmount
    useEffect(() => {
        return () => {
            clearNoteTypes();
        };
    }, [clearNoteTypes]);

    const createTable = useMemo(() => {
        if (!noteTypes || noteTypes.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no note types to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = noteTypes.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={noteTypes}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : noteTypes.length
                    }
                    pageSize={showPagination ? pageSize : noteTypes.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no noteTypes to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/general/notetypes/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [noteTypes, pageSize, PAGE_SIZE_OPTIONS, setPageSize, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Note Types</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.NoteTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/general/notetypes/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showNoteTypes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showNoteTypes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showNoteTypes, createTable]);

    return (
        <LookupsLayout
            loading={loadingNoteTypes}
            permission={Dtos.Permission.NoteTypesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<NoteTypesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        noteTypes: state.noteTypes.formFilteredData!,
        formFilter: state.noteTypes.formFilter,
        loadingNoteTypes:
            state.noteTypes.loadState &&
            state.noteTypes.loadState.status == RequestState.Pending,
        loadNoteTypesSuccess:
            state.noteTypes.loadState &&
            state.noteTypes.loadState.status == RequestState.Success,
        loadNoteTypesFailure:
            state.noteTypes.loadState &&
            state.noteTypes.loadState.status == RequestState.Failure,
        loadNoteTypesErrorCode: state.noteTypes.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<NoteTypesPageProps> => {
    return {
        loadNoteTypes: bindActionCreators(
            NoteTypeActions.LoadNoteType,
            dispatch
        ),
        clearNoteTypes: bindActionCreators(NoteTypeActions.Clear, dispatch),
        filter: bindActionCreators(NoteTypeActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(NoteTypesPage);
